import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Eye, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { previewMarketingLinkedIn } from '../../services/api';

const FIELDS = ['cover_hook', 'cover_sub', 'slide2_title', 'slide2_items', 'slide3_title', 'slide3_sub', 'slide3_cta'];

// Renders the exact 3 pages that would be uploaded, with validation results.
export const LinkedInCarouselPreview = ({ articleId, carousel, onFill }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const res = await previewMarketingLinkedIn(articleId, carousel);
      setData(res);
      return res;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build the carousel preview');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const fill = async () => {
    const res = await run(); // always fresh, so text typed since the last preview is kept
    if (!res) return;
    const empty = FIELDS.filter((k) => res.sources[k] === 'article');
    if (!empty.length) { toast.info('No empty fields to fill'); return; }
    onFill(Object.fromEntries(empty.map((k) => [k, res.carousel[k]])));
    toast.success(`Filled ${empty.length} field${empty.length === 1 ? '' : 's'} from the article. Review, then save.`);
  };

  return (
    <div className="mt-4 border border-slate-200 rounded-md p-3 space-y-3" data-testid="linkedin-carousel-preview">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="carousel-preview-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
          Preview carousel pages
        </Button>
        <Button size="sm" variant="outline" onClick={fill} disabled={busy} data-testid="carousel-fill-from-article-btn">
          <Wand2 className="h-4 w-4 mr-1.5" /> Fill empty fields from article
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        Empty fields are filled automatically from the article when posting. Placeholder or missing content blocks the LinkedIn post.
      </p>
      {data && (
        <>
          {data.ready ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="carousel-ready">
              <CheckCircle2 className="h-4 w-4" /> Ready to post
            </div>
          ) : (
            <div className="rounded bg-red-50 border border-red-200 p-2 text-sm text-red-800 space-y-1" data-testid="carousel-errors">
              <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" /> LinkedIn posting is blocked</div>
              <ul className="list-disc pl-5 text-xs space-y-0.5">
                {data.errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2" data-testid="carousel-pages">
            {data.pages.map((src, i) => (
              <a key={i} href={src} target="_blank" rel="noreferrer" data-testid={`carousel-page-${i + 1}`}>
                <img src={src} alt={`Carousel page ${i + 1}`} className="w-full rounded border border-slate-200" />
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LinkedInCarouselPreview;
